/**
 * PoolFahrzeugCard — Karten-Ansicht für ein einzelnes Poolfahrzeug (Issue #164).
 *
 * Zeigt: Bild/Platzhalter, Kennzeichen, Marke/Modell, aktuellen Status
 * ("frei bis …" oder "belegt von … bis …"), KM-Stand und einen
 * „Reservieren"-Button. Ein Admin-Button „Schlüssel ausgeben" erscheint nur
 * für Reservierungen mit gesetzter schluesselausgabe_am = null.
 */

import { AlertTriangle, Car, Key, CalendarClock, Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const KOOP_RED = '#C41E3A';

export interface PoolReservierung {
  id: string;
  fahrzeug_id?: string;
  mitarbeiter_id: string | null;
  fahrer_mitarbeiter_id: string | null;
  beginn: string | null;
  ende: string | null;
  zweck: string | null;
  schluesselausgabe_am: string | null;
  storniert_am: string | null;
}

export interface PoolFahrzeug {
  id: string;
  marke: string;
  modell: string;
  kennzeichen: string | null;
  bild_url: string | null;
  km_aktuell: number | null;
  nutzungsart: string;
  status: string;
  aktuelle_reservierung?: PoolReservierung | null;
  naechste_reservierung?: PoolReservierung | null;
  letzte_abgeschlossene_reservierung?: PoolReservierung | null;
}

interface PoolFahrzeugCardProps {
  fahrzeug: PoolFahrzeug;
  onReservieren: (fahrzeugId: string) => void;
  onSchluesselAusgabe: (reservierungId: string) => void;
  onSchadenMelden?: (reservierungId: string) => void;
  isAdmin?: boolean;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function StatusBadge({ reservierung }: { reservierung: PoolReservierung | null | undefined }) {
  const now = Date.now();
  if (!reservierung || !reservierung.beginn || !reservierung.ende) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
        Frei
      </Badge>
    );
  }
  const beginn = new Date(reservierung.beginn).getTime();
  const ende = new Date(reservierung.ende).getTime();
  if (now >= beginn && now < ende) {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
        Belegt bis {formatDatum(reservierung.ende)}
      </Badge>
    );
  }
  if (now < beginn) {
    return (
      <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">
        Reserviert ab {formatDatum(reservierung.beginn)}
      </Badge>
    );
  }
  return (
    <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
      Frei
    </Badge>
  );
}

export function PoolFahrzeugCard({
  fahrzeug,
  onReservieren,
  onSchluesselAusgabe,
  onSchadenMelden,
  isAdmin = false,
}: PoolFahrzeugCardProps) {
  const aktRes = fahrzeug.aktuelle_reservierung;
  const now = Date.now();
  const isBelegt =
    aktRes &&
    aktRes.beginn &&
    aktRes.ende &&
    now >= new Date(aktRes.beginn).getTime() &&
    now < new Date(aktRes.ende).getTime();

  const schluesselFaellig =
    isAdmin &&
    aktRes &&
    aktRes.beginn &&
    !aktRes.schluesselausgabe_am &&
    now >= new Date(aktRes.beginn).getTime() - 30 * 60 * 1000; // 30 min vor Beginn

  return (
    <div
      data-testid="pool-fahrzeug-card"
      className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col"
    >
      {/* Fahrzeug-Bild */}
      <div className="h-36 bg-gray-100 flex items-center justify-center overflow-hidden">
        {fahrzeug.bild_url ? (
          <img
            src={fahrzeug.bild_url}
            alt={`${fahrzeug.marke} ${fahrzeug.modell}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <Car className="size-14 text-gray-300" />
        )}
      </div>

      {/* Inhalt */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-sm leading-tight">
              {fahrzeug.marke} {fahrzeug.modell}
            </p>
            {fahrzeug.kennzeichen && (
              <p className="text-xs text-gray-500 font-mono">{fahrzeug.kennzeichen}</p>
            )}
          </div>
          <StatusBadge reservierung={aktRes} />
        </div>

        {fahrzeug.km_aktuell !== null && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Gauge className="size-3.5" />
            <span>{fahrzeug.km_aktuell.toLocaleString('de-DE')} km</span>
          </div>
        )}

        {fahrzeug.naechste_reservierung?.beginn && !isBelegt && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <CalendarClock className="size-3.5" />
            <span>Nächste: {formatDatum(fahrzeug.naechste_reservierung.beginn)}</span>
          </div>
        )}

        {/* Aktionen */}
        <div className="mt-auto pt-2 flex flex-col gap-2">
          <Button
            size="sm"
            disabled={!!isBelegt}
            onClick={() => onReservieren(fahrzeug.id)}
            style={isBelegt ? undefined : { backgroundColor: KOOP_RED, color: 'white', border: 'none' }}
            className={isBelegt ? 'bg-gray-100 text-gray-400 border border-gray-200' : ''}
          >
            {isBelegt ? 'Belegt' : 'Reservieren'}
          </Button>

          {schluesselFaellig && aktRes && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="schluessel-ausgabe-btn"
              onClick={() => onSchluesselAusgabe(aktRes.id)}
            >
              <Key className="size-3.5 mr-1" />
              Schlüssel ausgeben
            </Button>
          )}

          {(() => {
            const schadenRes = (aktRes && !aktRes.storniert_am)
              ? aktRes
              : (!aktRes && fahrzeug.letzte_abgeschlossene_reservierung && !fahrzeug.letzte_abgeschlossene_reservierung.storniert_am)
                ? fahrzeug.letzte_abgeschlossene_reservierung
                : null;
            return schadenRes && onSchadenMelden ? (
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="schaden-melden-btn"
                onClick={() => onSchadenMelden(schadenRes.id)}
              >
                <AlertTriangle className="size-3.5 mr-1" />
                Schaden melden
              </Button>
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
}
